import { useState } from 'react'
import { X, MoreVertical } from 'lucide-react'
import { STATUS_CONFIG } from '../../lib/constants'
import { useSettings } from '../../lib/settings'

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({ title, onClose, children, footer, size = '' }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${size === 'lg' ? 'modal-lg' : ''}`}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  )
}

// ── Dot menu ──────────────────────────────────────────────────────────────────
export function DotMenu({ onEdit, onDelete }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="dot-menu-wrap" onClick={e => e.stopPropagation()}>
      <button className="dot-menu-btn" onClick={() => setOpen(o => !o)}>
        <MoreVertical size={13} />
      </button>
      {open && (
        <div className="dot-menu-dropdown">
          <div className="dot-menu-item" onClick={() => { setOpen(false); onEdit() }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Edit
          </div>
          <div className="dot-menu-item danger" onClick={() => { setOpen(false); onDelete() }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
            Delete
          </div>
        </div>
      )}
    </div>
  )
}

// ── Status badge ──────────────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Considering
  return <span className="badge" style={{ color: cfg.color, background: cfg.bg }}>{status}</span>
}

// ── Type badge ────────────────────────────────────────────────────────────────
export function TypeBadge({ type }) {
  const { categories } = useSettings()
  const cat = categories.find(c => c.key === type)
  if (!cat) return null
  return (
    <span className="badge" style={{ color: cat.color, background: cat.color + '18' }}>
      {cat.emoji} {type}
    </span>
  )
}

// ── Toggle switch ─────────────────────────────────────────────────────────────
export function Toggle({ checked, onChange }) {
  return (
    <label className="toggle-switch">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <div className="toggle-track" />
      <div className="toggle-thumb" />
    </label>
  )
}

// ── Form helpers ──────────────────────────────────────────────────────────────
export function Field({ label, hint, children }) {
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      {children}
      {hint && <span className="form-hint">{hint}</span>}
    </div>
  )
}

export function Input(props) {
  return <input className="form-input" {...props} />
}

export function Select({ children, ...props }) {
  return <select className="form-select" {...props}>{children}</select>
}

export function Textarea(props) {
  return <textarea className="form-textarea" {...props} />
}

// ── Confirm dialog ────────────────────────────────────────────────────────────
export function ConfirmDialog({ title, message, onConfirm, onCancel, loading }) {
  return (
    <Modal title={title} onClose={onCancel} footer={
      <>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="btn btn-danger" onClick={onConfirm} disabled={loading}>
          {loading ? 'Deleting…' : 'Delete'}
        </button>
      </>
    }>
      <p className="confirm-message">{message}</p>
    </Modal>
  )
}

// ── Formatters ────────────────────────────────────────────────────────────────
export function formatCurrency(val) {
  if (!val && val !== 0) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
}

// ── Loading ───────────────────────────────────────────────────────────────────
export function Loading() {
  return (
    <div className="loading">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 12a9 9 0 11-6.219-8.56">
          <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur=".8s" repeatCount="indefinite"/>
        </path>
      </svg>
      Loading…
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon}</div>
      <div className="empty-title">{title}</div>
      {description && <div className="empty-desc">{description}</div>}
      {action}
    </div>
  )
}

// ── Urgency badge ─────────────────────────────────────────────────────────────
export function UrgencyBadge({ days, dateStr }) {
  const today = new Date()
  today.setHours(0,0,0,0)
  let label, color, bg
  if (days === null)  { label = 'No date';         color = '#9E9E9E'; bg = '#F5F5F5' }
  else if (days < 0)  { label = `${Math.abs(days)}d overdue`; color = '#B91C1C'; bg = '#FEF2F2' }
  else if (days === 0){ label = 'Due today';        color = '#B91C1C'; bg = '#FEF2F2' }
  else if (days <= 14){ label = `Due in ${days}d`;  color = '#B45309'; bg = '#FFFBEB' }
  else if (days <= 90){ label = `Due in ${days}d`;  color = '#15803D'; bg = '#F0FDF4' }
  else {
    const d = new Date(dateStr)
    label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    color = '#6B7280'; bg = '#F3F4F6'
  }
  return <span className="badge" style={{ color, background: bg }}>{label}</span>
}
